/**
 * `<video>` 첫 프레임 전 로딩 표시 판정 — 순수 함수 (vitest). BOOTH-93.
 *
 * - **첫 로드만 표시, 재버퍼는 표시하지 않는다.** `waiting`/`stalled` 는 Safari 가 루프 경계·네트워크 idle 에서
 *   스퓨리어스로 내 루프마다 깜빡이므로 무시한다. `loadstart`/`emptied`(src 교체) 만 다시 켠다.
 * - `until: "frame"`(기본) 은 HAVE_CURRENT_DATA(2) 이상 = 첫 프레임 디코딩, `"metadata"` 는 HAVE_METADATA(1) 이상.
 *   `preload="metadata"` + controls 로 자동재생하지 않는 영상은 Safari 가 재생 전까지 2 에 못 가므로 metadata 모드.
 * - 콘솔 `apps/booth-console/src/hooks/videoLoading.ts` 와 같은 코드(공유 UI 패키지 없음 — 고치면 양쪽 함께).
 */
export type VideoLoadEvent =
  | "loadstart"
  | "emptied"
  | "loadedmetadata"
  | "loadeddata"
  | "canplay"
  | "playing"
  | "error"
  | "waiting"
  | "stalled";

export type VideoLoadUntil = "frame" | "metadata";

export const VIDEO_LOAD_EVENTS: readonly VideoLoadEvent[] = [
  "loadstart",
  "emptied",
  "loadedmetadata",
  "loadeddata",
  "canplay",
  "playing",
  "error",
  "waiting",
  "stalled",
];

export function isVideoLoadEvent(type: string): type is VideoLoadEvent {
  return (VIDEO_LOAD_EVENTS as readonly string[]).includes(type);
}

/** HTMLMediaElement.readyState 가 표시를 끌 만큼 진행됐는가 — frame: ≥ HAVE_CURRENT_DATA(2), metadata: ≥ HAVE_METADATA(1) */
export function isVideoReady(readyState: number, until: VideoLoadUntil = "frame"): boolean {
  return readyState >= (until === "metadata" ? 1 : 2);
}

/** 이벤트 하나로 loading 을 갱신. waiting/stalled 는 prev 유지(재버퍼 표시 안 함). */
export function nextVideoLoading(prev: boolean, event: VideoLoadEvent, until: VideoLoadUntil = "frame"): boolean {
  switch (event) {
    case "loadstart":
    case "emptied":
      return true;
    case "loadeddata":
    case "canplay":
    case "playing":
    case "error":
      return false;
    case "loadedmetadata":
      return until === "metadata" ? false : prev;
    case "waiting":
    case "stalled":
      return prev;
  }
}
